import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/services/appApi';
import { googleAuth, googleNative, isGoogleAndroidPlatform, isGoogleNativePlatform, type GoogleStep, type GoogleIntent } from '@/services/googleAuth';
import { renderGoogleWebButton } from '@/services/googleWeb';
import forexMindLogo from '@/assets/forexmind-logo.png';
import { AGREEMENTS } from '../agreementContent';
import AgreementModal from './AgreementModal';

interface Props { intent?: GoogleIntent; email?: string; onClose: () => void; onSuccess: () => void }
function GoogleCodeInput({ value, onChange, disabled }: { value: string; onChange: (value: string) => void; disabled: boolean }) {
  return <div className="relative mb-7 flex items-center gap-2.5" onClick={() => document.getElementById('google-verification-code')?.focus()}>
    <input id="google-verification-code" aria-label="输入 6 位验证码" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
      disabled={disabled} value={value} onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      className="absolute inset-0 z-10 h-full w-full border-0 bg-transparent p-0 text-transparent outline-none" style={{ caretColor: 'transparent' }} />
    {Array.from({ length: 6 }, (_, index) => <div key={index} aria-hidden="true"
      className="flex h-14 min-w-0 flex-1 items-center justify-center rounded-xl border-[1.5px] text-[22px] font-semibold"
      style={{ background: value[index] ? '#F5F3FF' : '#FAFAFB', borderColor: value[index] ? '#4F46E5' : '#E5E7EB', color: '#4F46E5' }}>
      {value[index] || ''}
    </div>)}
  </div>;
}

export default function GoogleAuthFlow({ intent = 'login', email, onClose, onSuccess }: Props) {
  const { accessToken, acceptGoogleSession } = useAuth();
  const [step, setStep] = useState<GoogleStep | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [revocationPending, setRevocationPending] = useState(false);
  const [webReady, setWebReady] = useState(false);
  const [expired, setExpired] = useState(false);
  const webButton = useRef<HTMLDivElement>(null);
  const expiryTimer = useRef<number | undefined>(undefined);
  const exchanging = useRef(false);
  const token = useRef('');
  const alive = useRef(true);
  const callbacks = useRef({ onClose, onSuccess, acceptGoogleSession });
  callbacks.current = { onClose, onSuccess, acceptGoogleSession };

  function showError(e: unknown, fallback: string) {
    if (!alive.current) return;
    const code = e instanceof ApiError ? e.code : '';
    setErrorCode(code);
    setError(e instanceof Error ? e.message : fallback);
    if (code === 'GOOGLE_ATTEMPT_EXPIRED') { token.current = ''; setExpired(true); setWebReady(false); }
  }

  async function apply(next: GoogleStep) {
    if (!alive.current) return;
    if (next.status === 'authenticated' || next.status === 'bound' || next.status === 'unbound') {
      window.clearTimeout(expiryTimer.current);
      token.current = '';
    }
    if (next.status === 'authenticated') {
      await callbacks.current.acceptGoogleSession(next.auth);
      callbacks.current.onSuccess();
    } else if (next.status === 'bound') {
      callbacks.current.onSuccess();
    } else if (next.status === 'unbound') {
      setStep(null);
      setCode('');
      setWebReady(false);
      setExpired(false);
      if (!isGoogleNativePlatform() || isGoogleAndroidPlatform()) { setRevocationPending(true); return; }
      try { await googleNative.disconnect(); callbacks.current.onSuccess(); }
      catch { if (alive.current) setRevocationPending(true); }
    } else {
      setStep(next); setCode('');
    }
  }

  useEffect(() => {
    alive.current = true;
    const kickoff = window.setTimeout(() => {
      void (async () => {
        try {
          const start = await googleAuth.start(intent, accessToken, email);
          if (!alive.current) return;
          token.current = start.attempt_token;
          expiryTimer.current = window.setTimeout(() => { token.current = ''; if (isGoogleAndroidPlatform()) void googleNative.cancelSignIn().catch(() => {}); setExpired(true); setWebReady(false); setError('请返回并重新开始'); setBusy(false); }, start.expires_in * 1000);
          if (start.step) { await apply(start.step); return; }
          if (!start.nonce) throw new Error('无法开始 Google 登录');
          if (!isGoogleNativePlatform()) {
            if (!start.client_id || !webButton.current) throw new Error('Google 登录尚未配置');
            setWebReady(true);
            await renderGoogleWebButton(webButton.current, start.client_id, start.nonce, credential => {
              if (exchanging.current || !token.current) return;
              exchanging.current = true; setBusy(true); setError(''); setWebReady(false);
              void googleAuth.exchange(token.current, credential).then(apply).catch(e => {
                showError(e, 'Google 身份验证失败，请返回重试');
              }).finally(() => { exchanging.current = false; if (alive.current) setBusy(false); });
            }, () => alive.current && Boolean(token.current));
            return;
          }
          const result = await googleNative.signIn({ nonce: start.nonce, serverClientId: start.client_id ?? undefined });
          if (!alive.current || !token.current) return;
          await apply(await googleAuth.exchange(token.current, result.idToken));
        } catch (e) {
          if (!alive.current) return;
          if ((e as { code?: string }).code === 'GOOGLE_CANCELLED') callbacks.current.onClose();
          else showError(e, 'Google 登录暂不可用，请返回重试或使用邮箱登录');
        } finally { if (alive.current) setBusy(false); }
      })();
    }, 0);
    return () => { window.clearTimeout(kickoff); alive.current = false; token.current = ''; window.clearTimeout(expiryTimer.current); if (isGoogleAndroidPlatform()) void googleNative.cancelSignIn().catch(() => {}); };
    // A mounted flow is a single attempt; callbacks live in refs and credentials never persist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown(v => Math.max(v - 1, 0)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function submit(fields: Parameters<typeof googleAuth.complete>[1]) {
    if (busy || expired) return;
    setBusy(true); setError(''); setErrorCode('');
    try { await apply(await googleAuth.complete(token.current, fields)); }
    catch (e) { showError(e, '验证失败，请重试'); }
    finally { if (alive.current) setBusy(false); }
  }
  async function sendEmail() {
    if (busy || expired || cooldown > 0) return;
    setBusy(true); setError(''); setErrorCode('');
    try { const r = await googleAuth.sendEmail(token.current); if (alive.current) { setSent(true); setCooldown(r.rate_seconds); } }
    catch (e) { showError(e, '验证码发送失败'); }
    finally { if (alive.current) setBusy(false); }
  }

  const conflict = errorCode === 'GOOGLE_IDENTITY_CONFLICT';
  const terminalError = expired || conflict || errorCode === 'GOOGLE_ATTEMPT_STATE_CHANGED';
  const title = expired ? '本次登录已过期' : conflict ? '暂时无法绑定' : revocationPending ? '平台绑定已解除'
    : step?.status === 'needs_email' ? '验证已有账号' : step?.status === 'needs_totp' ? '安全验证'
      : error && !step ? '授权尚未完成' : intent === 'unlink' ? '解除 Google 绑定' : '使用 Google 继续';
  const canVerify = !busy && code.length === 6 && (step?.status !== 'needs_email' || sent);

  return <div role="dialog" aria-modal="true" aria-label="Google 账号验证" className="fixed inset-0 z-[100] overflow-y-auto bg-white px-6" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 72px)', paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 32px)' }}>
    <button type="button" onClick={onClose} aria-label="返回" className="absolute left-[18px] top-[max(env(safe-area-inset-top,0px),18px)] flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-[0_4px_12px_rgba(15,23,42,0.08)]">
      <i className="ri-arrow-left-s-line text-2xl" />
    </button>
    <div className="mx-auto max-w-sm">
      <div className="mb-10 flex flex-col items-center">
        <img src={forexMindLogo} alt="ForexMind" className="mb-3 h-11 w-11 object-contain drop-shadow-md" />
        <span className="text-[22px] font-bold tracking-tight text-gray-900">ForexMind</span>
      </div>
      <h2 className="mb-2 text-xl font-semibold tracking-tight text-gray-900">{title}</h2>
      {expired && <p role="alert" className="mb-7 text-sm text-gray-500">为保护账号，本次验证已结束。请返回并重新开始。</p>}
      {conflict && <p role="alert" className="mb-7 text-sm text-gray-500">该 Google 身份已关联其他账号。平台不会自动合并账号，也不会改变原有绑定。您可以返回使用邮箱登录。</p>}
      {error && !terminalError && <p role="alert" className="mb-5 text-sm text-red-600">{error}</p>}
      {(terminalError || (error && !step)) && <button type="button" onClick={onClose}
        className="mt-2 h-14 w-full rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-base font-semibold text-white shadow-[0_4px_14px_rgba(79,70,229,0.25)]">{intent === 'login' ? '返回邮箱登录' : '返回安全中心'}</button>}
      <div ref={webButton} className={webReady && !busy && !expired ? 'my-6' : 'hidden'} />
      {webReady && !busy && !expired && <p role="status" className="my-4 text-sm text-gray-500">请选择 Google 账号。关闭授权窗口后可点击返回，不会创建登录会话。</p>}
      {busy && <p role="status" className="my-5 text-sm text-gray-500">正在验证身份…</p>}
      {!terminalError && step?.status === 'needs_terms' && <>
        <p className="my-4 text-gray-600">请阅读并确认协议（{step.terms_version}）</p>
        <AgreementModal docs={AGREEMENTS} onCancel={onClose} busy={busy} error={error} onConfirm={() => { void submit({ agreed_terms: true, terms_version: step.terms_version }); }} />
      </>}
      {!terminalError && (step?.status === 'needs_email' || step?.status === 'needs_totp') && <>
        <p className={`${step.status === 'needs_email' ? 'mb-12' : 'mb-7'} text-sm text-gray-500`}>{step.status === 'needs_email' ? `请验证原账号邮箱 ${step.email_masked}。完成后关联 Google，保留现有账号和跟随关系。` : '请输入身份验证器中的 6 位验证码。Google 授权不会跳过平台安全验证。'}</p>
        {step.status === 'needs_email' && <p className="mb-2 text-[13px] font-medium text-gray-500">邮箱验证码</p>}
        <GoogleCodeInput value={code} onChange={setCode} disabled={busy} />
        <button type="button" disabled={!canVerify} onClick={() => void submit(step.status === 'needs_email' ? { otp: code } : { totp_code: code })}
          className="h-14 w-full rounded-2xl text-base font-semibold text-white disabled:cursor-not-allowed disabled:text-gray-400"
          style={{ background: canVerify ? 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)' : '#E5E7EB', boxShadow: canVerify ? '0 4px 14px rgba(79,70,229,0.25)' : 'none' }}>验证并继续</button>
        {step.status === 'needs_email' && <div className="mt-6 flex items-center justify-center gap-1 text-sm text-gray-400">
          <span>未收到验证码？</span><button type="button" disabled={busy || cooldown > 0} onClick={() => void sendEmail()} className="min-h-11 text-indigo-600 disabled:text-gray-400">{cooldown ? `${cooldown}s 后重新发送` : sent ? '重新发送' : '发送验证码'}</button>
        </div>}
      </>}
      {revocationPending && <>
        <p role="status" className="my-5 text-sm text-gray-600">Google 授权撤销未完成。您仍可使用邮箱验证码登录，账户及跟随关系保持不变。</p>
        {isGoogleNativePlatform() && !isGoogleAndroidPlatform() ? <button type="button" className="mt-4 block min-h-11 text-indigo-600" onClick={() => { void googleNative.disconnect().then(onSuccess).catch(() => setError('暂时无法撤销 Google 授权，请稍后再试')); }}>重试撤销授权</button> : <a href="https://myaccount.google.com/connections" target="_blank" rel="noopener noreferrer" className="mt-4 block min-h-11 text-indigo-600">前往 Google 管理授权</a>}
        <button type="button" className="mt-4 block min-h-11 text-gray-600" onClick={onSuccess}>完成</button>
        <p className="mt-4 text-xs text-gray-400">返回安全中心后可继续使用平台账号。</p>
      </>}
    </div>
  </div>;
}
